// What a database NAME identifies on a connection — the key the navigational
// lifecycle files an index under, and the key any "same database?" comparison
// has to use.
//
// Lives in its own module (rather than in `navigationalLifecycle.ts`, where it
// started) because three layers need it and only one of them may import the
// lifecycle: the lifecycle itself, the DDL invalidator's key policy, and the
// References tab's reuse test. It depends only on the per-connection CASE
// PROBES (`mssqlCollationProbe.ts`, `mysqlCaseProbe.ts`), neither of which
// imports the lifecycle — so nothing here can pull it back in through a cycle.

import type { DatabaseEngineId } from '@/core/engines';

import { mssqlDatabaseNamesCaseSensitive } from '../mssqlCollationProbe';
import { mysqlObjectKeyCase } from '../mysqlCaseProbe';

/**
 * The identity a database name has on this connection. Per engine:
 *
 * - `pgsql` — the EXACT name. PostgreSQL database names are case-sensitive
 *   identifiers: `"Sales"` and `sales` can coexist on one server and the app
 *   routes them to different pools.
 * - `mssql` — the exact name on an instance whose SERVER collation is
 *   case-sensitive (`Sales` and `sales` are then two databases), the
 *   lower-cased name otherwise — the usual `_CI_` instance has one `Sales`
 *   however the Object Explorer or the connection dialog spells it. The
 *   collation is probed before any key is computed (`navCasePolicy.ts`).
 * - `mysql` / `oracle` / `sqlite` — ONE identity for the whole connection,
 *   whatever name a reader passes, because the INDEX does not depend on it:
 *   MySQL's and Oracle's listings enumerate every schema the login can see
 *   (`information_schema` / `ALL_*`), and a SQLite connection IS one file —
 *   the explorer calls that file `main` while the connection stores its path,
 *   so the same index would otherwise be built twice under two names. Keying
 *   by name built one identical copy per name asked about, registered each
 *   with the DDL invalidator, and re-ran every DDL refresh once per copy.
 *   The lifecycle entry keeps `database` as the query-ROUTING value.
 */
export function databaseIdentityKey(
  connectionId: string,
  engineId: DatabaseEngineId,
  database: string,
): string {
  if (engineId === 'pgsql') return database;
  if (engineId === 'mysql' || engineId === 'oracle' || engineId === 'sqlite') return '';
  if (engineId === 'mssql' && mssqlDatabaseNamesCaseSensitive(connectionId) === true) return database;
  return database.toLowerCase();
}

/**
 * The key a CONSUMER may compare two database names by — a References tab
 * deciding whether to reuse itself, a cache deciding whether two requests are
 * the same. It is NOT {@link databaseIdentityKey}, and the two differences are
 * both cases where the index identity is deliberately coarser than the
 * REQUEST:
 *
 * - On MySQL / Oracle / SQLite the index identity is `''` because one index
 *   spans every schema — but a request still resolves a bare `users` against
 *   the database it names (the implicit schema), so `DbA` and `DbB` give
 *   different answers. A tab reused across them would show one database's
 *   results under the other's header, routing and cached bodies.
 * - On SQL Server, while the instance's collation is still UNKNOWN (the narrow
 *   window right after a connect, before the probe lands) the answer to "is
 *   `Sales` the same database as `sales`?" is not known either. Comparing
 *   exactly errs toward a second tab, which is harmless; folding errs toward
 *   reusing the WRONG tab, which is not.
 *
 * The CASE rule it applies is not free either — it has to be the rule the
 * Object-Explorer gate applies (`go-to/oeTreeQualification.ts`'s
 * `contextDatabaseEquals` / `databaseComponentEquals`), or one search over one
 * index opens two tabs. The two agree on every engine, with no divergence
 * recorded. Per engine, and why:
 *
 * - `oracle` — EXACT, like pg. An Oracle "database" IS the owner, and every
 *   OWNER name that reaches a reader is a CATALOG spelling: the Object
 *   Explorer and the database dropdown both list `ALL_USERS`
 *   (`database-objects/services/queries/oracleQueries.ts`), and a configured
 *   `oracleInitialSchema` is quoted VERBATIM into
 *   `ALTER SESSION SET CURRENT_SCHEMA = "…"` by the pool
 *   (`electron/oracle-connection/oraclePoolFactory.ts`) and by every execution
 *   (`electron/query-engine-adapters/oracleAdapter.ts`), so a connection
 *   configured with a spelling the catalog does not hold never opens at all.
 *   The exact compare is also the only one that can tell `"Sales"` from
 *   `SALES`, which really are two owners once one was created quoted. Rounds
 *   11–12 UPPER-folded this (and then narrowed the fold by reading the
 *   catalog); round 13 removed both — see the git history of this file for the
 *   scenario that turned out to be unreachable.
 *
 *   This paragraph used to claim `oracleInitialSchema` was the ONLY configured
 *   value that can reach a tab. It is not: several tab-creation paths fall back
 *   to `connection.database` when no Object-Explorer database is selected
 *   (`workspace/start-page/utils/quickActions.ts`,
 *   `main-toolbar/hooks/useToolbarFileActions.ts`,
 *   `database-objects/services/newObjectScriptService.ts` and the object
 *   context-menu services), and on Oracle that field is the SERVICE-name slot
 *   (`connection-lifecycle/navLifecycleDatabaseResolution.ts`'s
 *   `lifecycleDatabaseOf` has the full reading). It changes nothing HERE — a
 *   service name is not a differently-CASED owner, so no fold would make it
 *   match an owner either, and the exact compare stands — but it is a real
 *   exposure one layer up and must not be written down as impossible.
 * - `mysql` — the schema rule, because a MySQL schema IS a database and a
 *   database is a DIRECTORY on disk: exact on `@@lower_case_table_names = 0`,
 *   folded on `1` / `2`. Read through `mysqlObjectKeyCase` — the rule the
 *   identifier index keys with — so this cannot drift away from the index it
 *   compares against. An unprobed connection keeps the exact compare for the
 *   SQL Server reason above (a second tab is harmless, the wrong tab is not);
 *   the lifecycle's gate (`navCasePolicy.ts`) makes sure a live index never
 *   sees that window.
 * - `pgsql` / `sqlite` — exact. PostgreSQL database names are case-sensitive
 *   identifiers routed to different pools, and a SQLite "database" is a file
 *   path, not an identifier at all.
 */
export function databaseComparisonKey(
  connectionId: string,
  engineId: DatabaseEngineId,
  database: string,
): string {
  if (engineId === 'mysql') {
    return mysqlObjectKeyCase(connectionId, 'view')?.schema === false
      ? database.toLowerCase()
      : database;
  }
  if (engineId !== 'mssql') return database;
  const caseSensitive = mssqlDatabaseNamesCaseSensitive(connectionId);
  if (caseSensitive === undefined) return database;
  return caseSensitive ? database : database.toLowerCase();
}
